import { ThumbsDown, ThumbsUp, UserCheck } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import DeleteButton from '../../../../components/common/DeleteButton'
import Drawer from '../../../../components/common/Drawer'
import Label from '../../../../components/form/Label'
import RichText from '../../../../components/form/RichText/Index'
import Select from '../../../../components/form/Select'
import DateField from '../../../../components/form/input/DateField'
import { api } from '../../../../configs/api'
import { useAuth } from '../../../../context/AuthContext'
import { useBusiness } from '../../../../context/BusinessContext'
import { useCompany } from '../../../../context/CompanyContext'
import BusinessNoteDelete from './BusinessNoteDelete'
import { NoteType } from './BusinessNotes'
import { NotesProps } from '../../../../components/common/Notes'

const BusinessNoteForm = ({
    data,
    open,
    handleClose,
    openDelete,
    setOpenDelete,
    setData,
    isPositive,
    setIsPositive
}: {
    data: NoteType | null
    open: boolean
    handleClose: () => void
    openDelete: boolean
    setOpenDelete: (value: boolean) => void
    setData: (data: any) => void
    isPositive: boolean
    setIsPositive: (value: boolean) => void
}) => {
    const { unity, user, users } = useAuth()
    const { loadBusiness } = useBusiness()
    const { detail, getCompany } = useCompany()
    const form = useForm({ mode: 'onChange' })
    const formRef = useRef<HTMLFormElement>(null)
    const anotherUsers = users.filter(item => item.id !== user?.userID)

    const handleSave = async (value: any) => {
        if (!unity || !data) return

        const id = data.id
        if (!id) {
            return toast.error('Contato não encontrado!')
        }

        try {
            const values = {
                ...value,
                situation: isPositive ? 1 : 0,
                id
            }

            if (!values.date) {
                return toast.error('Por favor, preencha o campo data')
            }
            if (isPositive && !values.dateNotify) {
                return toast.error('Por favor, preencha o campo lembrete')
            }
            if (!values.description) {
                return toast.error('Por favor, preencha o campo descrição')
            }

            const response = await fetch(`${api}business/note/update`, {
                method: 'POST',
                body: JSON.stringify(values)
            })
            const result = await response.json()
            if (!result.success) return toast.error(result.message || 'Erro ao atualizar nota!')

            // Atualiza o status da nota no contexto corretamente
            setData((prevData: any) => ({
                ...prevData,
                notes: prevData.notes.map((item: NotesProps['data'][number]) =>
                    item.id === id
                        ? {
                              ...item,
                              ...values // ou ...result.note se o backend retornar os dados atualizados
                          }
                        : item
                )
            }))
            await loadBusiness()

            handleClose()
            toast.success(result.message || 'Nota atualizada com sucesso!')
        } catch (error) {
            console.log(error)
        }
    }

    // Atualiza os valores do formulário quando o contexto mudar
    useEffect(() => {
        if (data) {
            form.reset({
                date: data.date || '',
                description: data.description || '',
                dateNotify: data?.dateNotify || '',
                users: data?.users || []
            })
        }
    }, [data])

    if (!data) return null

    return (
        <>
            <Drawer
                open={open}
                handleClose={handleClose}
                title={
                    <div className='flex items-center gap-2'>
                        <span>Editar nota</span>
                        <DeleteButton handleClick={() => setOpenDelete(true)} />
                    </div>
                }
                handleSave={() => formRef.current?.requestSubmit()}
            >
                <form ref={formRef} onSubmit={form.handleSubmit(handleSave)}>
                    <div className='space-y-3'>
                        <div>
                            <Label>Data</Label>
                            <Controller
                                name='date'
                                control={form.control}
                                defaultValue={data.date}
                                render={({ field }) => (
                                    <DateField
                                        name='date'
                                        value={field.value || ''}
                                        onChange={field.onChange}
                                        setValue={form.setValue}
                                    />
                                )}
                            />
                        </div>

                        <div>
                            <Label>Encaminhar para</Label>
                            <Controller
                                name='users'
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <Select
                                        options={anotherUsers}
                                        onChange={field.onChange} // Atualiza o react-hook-form
                                        value={field.value || []} // Mantém o valor sincronizado
                                        allowNew
                                        key={'contact'}
                                        multiple
                                        icon={<UserCheck className='w-5 h-5' />}
                                        error={fieldState.error ? true : false}
                                    />
                                )}
                            />
                        </div>

                        <div className='flex items-start gap-4'>
                            <div>
                                <Label>Situação</Label>
                                <div
                                    className={`flex items-center gap-1 text-center mt-4 cursor-pointer ${
                                        isPositive ? 'text-green-400' : 'text-red-400'
                                    }`}
                                    onClick={() => setIsPositive(!isPositive)}
                                >
                                    {isPositive ? <ThumbsUp className='w-5 h-5' /> : <ThumbsDown className='w-5 h-5' />}
                                    <p>{isPositive ? 'Positivo' : 'Negativo'}</p>
                                </div>
                            </div>

                            <div className='w-full'>
                                <Label required={isPositive}>Lembrete</Label>
                                <Controller
                                    name='dateNotify'
                                    control={form.control}
                                    // rules={{ required: isPositive }}
                                    defaultValue={data.dateNotify}
                                    render={({ field }) => (
                                        <DateField
                                            name='dateNotify'
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            setValue={form.setValue}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Nota</Label>
                            <Controller
                                name='description'
                                control={form.control}
                                render={({ field }) => <RichText value={field.value} onChange={field.onChange} />}
                            />
                        </div>
                    </div>
                </form>
            </Drawer>

            <BusinessNoteDelete
                open={openDelete}
                handleClose={() => setOpenDelete(false)}
                handleCloseForm={handleClose}
                data={data}
                setData={setData}
            />
        </>
    )
}

export default BusinessNoteForm
